<template>
    <section id="main-header" class="header" :class="{compact: !isHome}">
        <div class="hero">
            <div v-if="isHome" class="hero-text">
                <h1 class="name">Alexander Gorbatovsky</h1>
                <p class="role">Senior Software Engineer</p>
                <p class="tagline">{{ yearsOfExperience }} years of full stack development: Vue.js, Node.js, Python and AI agents.</p>
                <div class="actions">
                    <a class="button" href="/CV/CV_Alexander_Gorbatovsky.pdf"
                       download="CV_Alexander_Gorbatovsky">Download CV</a>
                    <a class="button" href="#contact" @click.prevent="scrollToContact">Contact me</a>
                </div>
            </div>
            <img class="logo" alt="GorAlex logo" src="@/assets/logo-with-txt.svg">
        </div>
    </section>
</template>

<script>
export default {
    name: "HeaderComponent",
    computed: {
        // The full hero (name, role, CV and contact buttons) is for the home page only.
        isHome() {
            return this.$route.path === '/';
        },
        yearsOfExperience() {
            const startYear = 2016;
            return new Date().getFullYear() - startYear;
        }
    },
    methods: {
        scrollToContact() {
            const contact = document.getElementById('contact');
            if (contact) {
                contact.scrollIntoView({behavior: 'smooth'});
            }
        }
    }
}
</script>

<style lang="scss" scoped>

#main-header.header {
    display: flex;
    align-items: center;
    min-height: 34vh;
    padding: 2em 12vw;
    color: #fff;
    background-color: #25242c;
    background-image: linear-gradient(90deg, rgba(37, 36, 44, .92) 0%, rgba(37, 36, 44, .65) 55%, rgba(37, 36, 44, .35) 100%),
        url("../assets/headerImgBig.jpg");
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;

    &.compact {
        min-height: 22vh;
    }

    .hero {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 2em;
        width: 100%;

        // One orchestrated entrance on page load: each line rises in after the previous one.
        .hero-text > *, .logo {
            animation: hero-rise .7s ease-out both;
        }

        .hero-text {
            max-width: 640px;

            .name {
                margin: 0;
                font-family: 'Lobster', Lobster, Arial, sans-serif;
                font-weight: normal;
                font-size: 3.2em;
                line-height: 1.1;
                animation-delay: .1s;
            }

            .role {
                margin: .3em 0 0;
                font-size: 1.4em;
                font-weight: 500;
                animation-delay: .2s;
            }

            .tagline {
                margin: .6em 0 0;
                max-width: 32em;
                font-size: 1.05em;
                line-height: 1.5;
                color: #d0d3d8;
                animation-delay: .3s;
            }

            .actions {
                display: flex;
                flex-wrap: wrap;
                gap: 14px;
                margin-top: 1.5em;
                animation-delay: .4s;

                .button {
                    padding: 10px 26px;
                    color: #fff;
                    background-color: transparent;
                    border: 2px solid #fff;
                    border-radius: 999px;
                    font-weight: 500;
                    text-decoration: none;
                    transition: background-color .2s ease, color .2s ease;

                    &:hover, &:focus-visible {
                        color: #25242c;
                        background-color: #fff;
                        outline: none;
                    }
                }
            }
        }

        .logo {
            flex: none;
            width: 6.5em;
            animation-delay: .5s;
        }
    }
}

@keyframes hero-rise {
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    #main-header.header {
        .hero {
            .hero-text > *, .logo {
                animation: none;
            }
        }
    }
}

/*media*/
@media screen and (max-width: 1000px) {
    #main-header.header {
        padding: 2em 8vw;

        .hero {
            .hero-text {
                .name {
                    font-size: 2.8em;
                }
            }
        }
    }
}

@media screen and (max-width: 770px) {
    #main-header.header {
        min-height: 0;
        padding: 1.6em 1.4em 1.8em;

        // The sticky nav bar already carries the logo on phones, so the hero keeps only the text.
        &:not(.compact) {
            .hero {
                .logo {
                    display: none;
                }
            }
        }

        // Other pages have no hero text, so there the logo stays, just smaller.
        &.compact {
            min-height: 0;
            padding: 1em 1.4em;

            .hero {
                .logo {
                    width: 3.6em;
                }
            }
        }

        .hero {
            .hero-text {
                .name {
                    font-size: 2.2em;
                }

                .role {
                    font-size: 1.15em;
                }

                .tagline {
                    margin-top: .5em;
                    font-size: 1em;
                }

                .actions {
                    margin-top: 1.2em;
                }
            }
        }
    }
}
</style>
